import {
    Alert,
    Anchor,
    Button,
    Group,
    HoverCard,
    Loader,
    Stack,
    Text,
    UnstyledButton,
} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import {type ReactNode, useState} from 'react'

import {AssignmentStrip} from 'src/assignments/assignment-strip'
import {JOB_PEEK_ASSIGNMENT_STRIP_DATE_RANGE} from 'src/assignments/assignment-strip-utils'

import {useWorkerAssignments} from './use-workers'

type WorkerAssignmentPeekProps = {
    workerId: string
    workerName: string
    workerTypeName: string
    referenceDate: Date
    children: ReactNode
}

export function WorkerAssignmentPeek({
    workerId,
    workerName,
    workerTypeName,
    referenceDate,
    children,
}: WorkerAssignmentPeekProps) {
    const [fetchEnabled, setFetchEnabled] = useState(false)
    const {
        data: assignmentList,
        isLoading,
        isError,
        error,
        refetch,
        referenceDate: resolvedReferenceDate,
        dateRange,
    } = useWorkerAssignments(workerId, {
        enabled: fetchEnabled,
        referenceDate,
        dateRange: JOB_PEEK_ASSIGNMENT_STRIP_DATE_RANGE,
    })

    return (
        <HoverCard
            width={700}
            shadow="md"
            withArrow
            openDelay={200}
            closeDelay={100}
            onOpen={() => setFetchEnabled(true)}
            onClose={() => setFetchEnabled(false)}
        >
            <HoverCard.Target>
                <UnstyledButton style={{display: 'block', minWidth: 0, flex: 1}}>
                    {children}
                </UnstyledButton>
            </HoverCard.Target>
            <HoverCard.Dropdown p="md">
                <Stack gap="md">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Stack gap={2} style={{minWidth: 0}}>
                            <Text fw={600} truncate>
                                {workerName}
                            </Text>
                            <Text size="sm" c="dimmed" truncate>
                                {workerTypeName}
                            </Text>
                        </Stack>
                        <Anchor
                            component={Link}
                            to="/workers/$workerId"
                            params={{workerId}}
                            size="sm"
                            style={{flexShrink: 0}}
                        >
                            View full details
                        </Anchor>
                    </Group>

                    {isLoading && (
                        <Group justify="center" py="sm">
                            <Loader size="sm" />
                        </Group>
                    )}

                    {isError && (
                        <Alert color="red" title="Could not load assignments">
                            {error instanceof Error ? error.message : 'Something went wrong.'}
                            <Button
                                onClick={() => void refetch()}
                                variant="light"
                                size="compact-sm"
                                mt="sm"
                            >
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {assignmentList && !isLoading && !isError && (
                        <AssignmentStrip
                            assignments={assignmentList.assignments}
                            referenceDate={resolvedReferenceDate}
                            referenceDayLabel="Start"
                            dayInteraction="none"
                            dateRange={dateRange}
                            presentation="schedule"
                            showTitle={false}
                            showLegend={false}
                        />
                    )}
                </Stack>
            </HoverCard.Dropdown>
        </HoverCard>
    )
}
